// src/oura/components/docs/PickCodeDialog.tsx
//
// "Pick a code" for a file whose name carries none: which of the contract's
// codes it belongs to (one or several, #4892), with an outlet if it has one,
// or a doc type that files it under all of its codes (an Answer Plan is S1,
// S3, B1 and G4), or "Not a client doc" for internal files such as the
// tracker or a crawl doc. Oura files it there until the name carries a code
// of its own. It never renames the file. A pick made by mistake can go back
// to "No code yet".
//
// Drawer specs v1.1, A-B: a view inside the doc drawer (DocSheet), never a
// drawer of its own. From the doc's foot it is a sub-view ("‹ {doc title}",
// Cancel goes back); from a row's "Pick a code" it opens straight here and
// Cancel closes. The header (title, the doc's lead and status line) is the
// sheet's; this view draws the body and the foot. A save goes back to the
// doc with "Filed under S1, S4.", or Next once the doc has left its group.
//
// A list of checkboxes (#4892), four parts, each with a heading: Matches the
// file name, Doc types, Codes (an outlet under its code), Other. Any number
// of codes can be on. A doc type is a preset: it puts on exactly its codes,
// and reads as on while those are the codes on. The backend keeps one outlet
// per doc, so one variant of a code is on at a time ("S2" or "S2, Bukit
// Bintang") and never two outlets. The two Other rows clear the codes.

import { Fragment, useId, useMemo, useState } from "react";
import { CheckIcon, SearchIcon } from "lucide-react";
import { joinCodes, pickDocumentCodes, type BusinessDocuments, type PickCodeIn, type StaffDocument } from "@service/aeo/clientDocs";
import { OUTLET_INVALID_TEXT, isShortOutlet, outletOk, type DocExpectation } from "@service/aeo/clientDocsRules";
import { useDocCatalogue } from "./useDocCatalogue";
import { cn } from "../../lib/cn";
import { DrawerBlock, DrawerFoot, FOCUS_RING, FamilyChip, LATE_TEXT, PillButton, TYPE, type Family } from "@oura/ui";
import { codeFamily, codeLabel, codeRowName, docsFailureText, pickableCodes } from "@oura/docs";

// Wide enough for a place name, and NOT upper-cased: "Mont Kiara" keeps its
// case and the backend refuses "mk" (#3973).
const FIELD = cn(
    TYPE.tb,
    "block h-9 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:w-56",
);
const FIND = cn(
    TYPE.tb,
    "block h-11 w-full rounded-xl border-0 bg-gray-50 pl-9 pr-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9",
);

const INTERNAL = "__internal__";
const NONE = "__none__";
/** More options than this and the list gets a "Find a code" field. */
const FIND_FROM = 10;

/** The codes filed once per outlet (S2): only these ask for an outlet. */
const perOutletCodes = (catalogue: readonly DocExpectation[]) => new Set(catalogue.filter((e) => e.perOutlet).flatMap((e) => e.codes));
const DOC_PREFIX = "doc:";

type Part = "match" | "types" | "codes" | "other";

const PART_TITLE: Record<Part, string> = {
    match: "Matches the file name",
    types: "Doc types",
    codes: "Codes",
    other: "Other",
};

interface Option {
    /**
     * One per row: "S2", "S2@Bukit Bintang", "doc:Answer Plan", INTERNAL or
     * NONE. A place-name outlet does not ride on the code, so "S2" alone would
     * name both S2 rows.
     */
    key: string;
    /** "S2-MK", or "S2" for plain S2 and a place-name outlet: what Find matches. */
    label: string;
    part: Part;
    /** The codes the row files it under: 1, or a doc type's codes; [] for INTERNAL and NONE. */
    codes: string[];
    outlet: string | null;
    name: string;
    family: Family | null;
}

const codeKey = (code: string, outlet: string | null) => (outlet ? `${code}@${outlet}` : code);

/**
 * In the order they read: the doc type the file's name already says, the
 * other catalogue docs that cover several of the contract's codes (an Answer
 * Plan files under S1, S3, B1 and G4 at once, YW 29 Sep 2026), every contract
 * code once with each outlet in use under it, then "Not a client doc" and,
 * for a doc that already has a pick or is marked internal, "No code yet".
 */
function optionsFor(board: Pick<BusinessDocuments, "codes">, docType: string | null, canClear: boolean, catalogue: readonly DocExpectation[]): Option[] {
    const picks = pickableCodes(board);
    const inContract = new Set(picks.map((p) => p.code));
    const types = catalogue
        .map((e) => ({ e, codes: e.codes.filter((c) => inContract.has(c)) }))
        .filter(({ codes }) => codes.length > 1)
        .map(({ e, codes }): Option => ({
            key: `${DOC_PREFIX}${e.docType}`,
            label: e.docType,
            part: !!docType && docType.trim().toLowerCase() === e.docType.toLowerCase() ? "match" : "types",
            codes,
            outlet: null,
            name: e.docType,
            family: null,
        }));
    const out: Option[] = [...types.filter((t) => t.part === "match"), ...types.filter((t) => t.part === "types")];
    const seen = new Set<string>();
    for (const p of picks) {
        if (!seen.has(p.code)) {
            seen.add(p.code);
            out.push({ key: p.code, label: p.code, part: "codes", codes: [p.code], outlet: null, name: p.name ?? p.code, family: p.family });
        }
        // One outlet's code names that outlet, never the contract's "every outlet".
        const key = codeKey(p.code, p.outlet);
        if (p.outlet && !seen.has(key)) {
            seen.add(key);
            out.push({ key, label: p.label, part: "codes", codes: [p.code], outlet: p.outlet, name: p.name ? codeRowName(p.name, p.outlet) : p.label, family: p.family });
        }
    }
    out.push({ key: INTERNAL, label: "", part: "other", codes: [], outlet: null, name: "Not a client doc (internal)", family: null });
    if (canClear) out.push({ key: NONE, label: "", part: "other", codes: [], outlet: null, name: "No code yet", family: null });
    return out;
}

interface Picked {
    /** The code rows on, in the order they were put on. */
    keys: string[];
    internal: boolean;
    /** The typed outlet, for a per-outlet code on with no outlet row of its own. */
    outlet: string;
}

/**
 * What the doc already carries: internal, or its staff-picked codes, each on
 * the row for the doc's outlet when the code has one. An outlet with no row
 * of its own goes back into the typed field.
 */
function pickedFrom(doc: StaffDocument, options: readonly Option[]): Picked {
    if (doc.notClientDoc) return { keys: [], internal: true, outlet: "" };
    if (doc.codeSource !== "staff" || doc.codes.length === 0) return { keys: [], internal: false, outlet: "" };
    const has = (key: string) => options.some((o) => o.part === "codes" && o.key === key);
    let rowOutlet = false;
    const keys: string[] = [];
    for (const c of doc.codes) {
        if (doc.outlet && has(codeKey(c, doc.outlet))) {
            keys.push(codeKey(c, doc.outlet));
            rowOutlet = true;
        } else if (has(c)) {
            keys.push(c);
        }
    }
    return { keys, internal: false, outlet: doc.outlet && !rowOutlet ? doc.outlet : "" };
}

const sameCodes = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((c) => b.includes(c));

export function PickCodeView({
    doc,
    board,
    onCancel,
    onSaved,
    onBusy,
}: {
    doc: StaffDocument;
    board: Pick<BusinessDocuments, "codes">;
    /** Back to the doc (from its foot), or close (from a row). */
    onCancel: () => void;
    /** After a pick is saved: what was done, for the doc's foot ("Filed under S4."). */
    onSaved: (said: string) => void;
    onBusy?: (busy: boolean) => void;
}) {
    // A staff pick or an internal mark can be undone; a code from the page title is changed, not cleared.
    const hasPick = doc.notClientDoc || (doc.codeSource === "staff" && doc.codes.length > 0);
    const catalogue = useDocCatalogue();
    const options = useMemo(() => optionsFor(board, doc.docType, hasPick, catalogue), [board, doc.docType, hasPick, catalogue]);
    const perOutlet = useMemo(() => perOutletCodes(catalogue), [catalogue]);
    const [initial] = useState<Picked>(() => pickedFrom(doc, options));
    const [picked, setPicked] = useState<Picked>(initial);
    const [find, setFind] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [outletError, setOutletError] = useState<string | null>(null);
    const listId = useId();
    const helpId = useId();
    const findId = useId();
    const outletId = useId();
    const outletHelpId = useId();
    const outletErrorId = useId();

    // "Find a code" narrows the list by name or code; what is on stays on even when it is filtered out.
    const q = find.trim().toLowerCase();
    const shown = q
        ? options.filter((o) => o.name.toLowerCase().includes(q) || o.label.toLowerCase().includes(q) || o.codes.some((c) => c.toLowerCase().includes(q)))
        : options;

    const read = (p: Picked) => {
        const rows = p.internal ? [] : options.filter((o) => o.part === "codes" && p.keys.includes(o.key));
        const codes = rows.map((o) => o.codes[0]);
        const rowOutlet = rows.find((o) => o.outlet)?.outlet ?? null;
        // The typed outlet only while a per-outlet code is on with no outlet row.
        const typedFor = rowOutlet ? null : rows.find((o) => perOutlet.has(o.codes[0])) ?? null;
        // Trimmed, never re-cased: the backend refuses "mk" and keeps "Mont Kiara".
        const typed = typedFor ? p.outlet.trim() : "";
        return { rows, codes, typedFor, typed, outlet: rowOutlet ?? (typed || null) };
    };
    const now = read(picked);
    const said = (p: Picked) => {
        const r = read(p);
        return p.internal ? INTERNAL : [...r.codes].sort().join(",") + "|" + (r.outlet ?? "");
    };
    // Save wakes once the pick says something new: other codes, another outlet, or internal.
    const changed = said(picked) !== said(initial);
    const preset = options.find((o) => o.key.startsWith(DOC_PREFIX) && sameCodes(o.codes, now.codes)) ?? null;

    const isOn = (o: Option) => {
        if (o.key === INTERNAL) return picked.internal;
        if (o.key === NONE) return !picked.internal && now.rows.length === 0;
        if (o.key.startsWith(DOC_PREFIX)) return !picked.internal && sameCodes(o.codes, now.codes);
        return !picked.internal && picked.keys.includes(o.key);
    };

    // The typed outlet belongs to the row it was typed under: it goes once that
    // row is off or an outlet row comes on, and never comes back with plain S2.
    const typedForKey = now.typedFor?.key ?? null;
    const withKeys = (p: Picked, keys: string[]): Picked => {
        const keepTyped = typedForKey !== null && keys.includes(typedForKey) && !keys.some((k) => options.find((x) => x.key === k)?.outlet);
        return { keys, internal: false, outlet: keepTyped ? p.outlet : "" };
    };

    const toggle = (o: Option) => {
        const wasOn = isOn(o);
        setError(null);
        setOutletError(null);
        setPicked((p) => {
            if (o.key === INTERNAL) return { keys: [], internal: !p.internal, outlet: "" };
            if (o.key === NONE) return { keys: [], internal: false, outlet: "" };
            const keys = p.internal ? [] : p.keys;
            if (o.key.startsWith(DOC_PREFIX)) {
                if (wasOn) return withKeys(p, []);
                // Its codes, each keeping the outlet row already on for it.
                return withKeys(p, o.codes.map((c) => keys.find((k) => options.some((x) => x.key === k && x.codes[0] === c)) ?? c));
            }
            if (keys.includes(o.key)) return withKeys(p, keys.filter((k) => k !== o.key));
            // One variant per code, and one outlet per doc.
            const clash = (k: string) => {
                const x = options.find((y) => y.key === k);
                return !x || x.codes[0] === o.codes[0] || Boolean(o.outlet && x.outlet && x.outlet !== o.outlet);
            };
            return withKeys(p, [...keys.filter((k) => !clash(k)), o.key]);
        });
    };

    const save = async () => {
        // Save stays off until the pick says something new, so Enter in the outlet field can land here unchanged.
        if (busy || !changed) return;
        if (now.typed && !outletOk(now.typed)) {
            setOutletError(OUTLET_INVALID_TEXT);
            document.getElementById(outletId)?.focus();
            return;
        }
        const pick: PickCodeIn = picked.internal ? { notClientDoc: true } : { codes: now.codes, outlet: now.codes.length ? now.outlet : null };
        setBusy(true);
        onBusy?.(true);
        setError(null);
        const r = await pickDocumentCodes(doc.id, pick);
        setBusy(false);
        onBusy?.(false);
        if (!r.ok) {
            if (r.code === "outlet_invalid") setOutletError(r.message);
            else if (r.code === "unknown_code" && r.badCodes?.length) setError(`Oura doesn't know ${r.badCodes.join(", ")}.`);
            else setError(r.reason === "network" ? "The code did not save. Try again." : docsFailureText(r, "this document"));
            return;
        }
        const labels = now.rows.map((o) => codeLabel(o.codes[0], o.outlet ?? (o === now.typedFor ? now.outlet : null)));
        onSaved(
            picked.internal
                ? "Marked as not a client doc."
                : now.codes.length === 0
                    ? "Back to No code yet."
                    : preset
                        ? `Filed as the ${preset.name}, under ${joinCodes(preset.codes)}.`
                        : `Filed under ${joinCodes(labels)}.`,
        );
    };

    return (
        <>
            <DrawerBlock>
                <p id={listId} className="m-0 font-medium text-gray-900">
                    What is this file for?
                </p>
                <p id={helpId} className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                    Pick one code or several. Oura files it here until the file name carries a code.
                </p>
                {options.length > FIND_FROM && (
                    <div className="relative mt-3">
                        <label htmlFor={findId} className="sr-only">
                            Find a code
                        </label>
                        <SearchIcon aria-hidden size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            id={findId}
                            type="search"
                            value={find}
                            autoComplete="off"
                            placeholder="Find a code"
                            onChange={(e) => setFind(e.target.value)}
                            className={FIND}
                        />
                    </div>
                )}
                <div role="group" aria-labelledby={listId} aria-describedby={helpId} className="mt-2">
                    {shown.length === 0 && <p className={cn(TYPE.tc, "m-0 px-3 py-2 text-gray-500")}>No code matches &ldquo;{find.trim()}&rdquo;.</p>}
                    {shown.map((o, i) => {
                        const on = isOn(o);
                        const head = i === 0 || shown[i - 1].part !== o.part;
                        const nested = Boolean(o.outlet);
                        const typedHere = on && o === now.typedFor;
                        return (
                            <Fragment key={o.key}>
                                {head && (
                                    <p aria-hidden className={cn(TYPE.lab, "m-0 px-3 pb-1", i === 0 ? "pt-1" : "pt-4")}>
                                        {PART_TITLE[o.part]}
                                    </p>
                                )}
                                <button
                                    type="button"
                                    role="checkbox"
                                    aria-checked={on}
                                    onClick={() => toggle(o)}
                                    className={cn(
                                        "flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors motion-reduce:transition-none",
                                        on ? "bg-blue-50" : "hover:bg-gray-50",
                                        nested && "pl-10",
                                        FOCUS_RING,
                                    )}
                                >
                                    <span
                                        aria-hidden
                                        className={cn(
                                            "grid h-4 w-4 shrink-0 place-items-center rounded ring-1 ring-inset",
                                            on ? "bg-blue-600 ring-blue-600" : "bg-white ring-gray-300",
                                        )}
                                    >
                                        {on && <CheckIcon size={12} strokeWidth={3} className="text-white" />}
                                    </span>
                                    {o.key.startsWith(DOC_PREFIX) ? (
                                        <span className="min-w-0 flex-1">
                                            <span className={cn(TYPE.tb, "block break-words", on ? "text-gray-900" : "text-gray-700")}>{o.name}</span>
                                            <span className="mt-1 flex flex-wrap items-center gap-1">
                                                {o.codes.map((c) => (
                                                    <FamilyChip key={c} family={codeFamily(c)}>
                                                        {c}
                                                    </FamilyChip>
                                                ))}
                                            </span>
                                        </span>
                                    ) : (
                                        <>
                                            {o.family && o.codes[0] && <FamilyChip family={o.family}>{codeLabel(o.codes[0], o.outlet)}</FamilyChip>}
                                            <span className={cn(TYPE.tb, "min-w-0 flex-1 break-words", on ? "text-gray-900" : "text-gray-700")}>{o.name}</span>
                                        </>
                                    )}
                                </button>
                                {/* S2 takes an outlet: the field opens under its own option. */}
                                {typedHere && (
                                    <div className="pb-2 pl-10 pr-3 pt-1">
                                        <label htmlFor={outletId} className={cn(TYPE.tc, "m-0 block font-medium text-gray-900")}>
                                            Outlet, if it has one
                                        </label>
                                        <p id={outletHelpId} className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                                            Its place name, such as Mont Kiara, or 2 to 4 capital letters, such as MK. Leave it empty for none.
                                        </p>
                                        <div className="mt-2 flex items-center gap-2">
                                            {/* "S2-" only while the outlet could ride on the code; a place name is named beside it, not on it. */}
                                            <span aria-hidden className="inline-flex">
                                                <FamilyChip family={codeFamily(o.codes[0])}>{now.typed && !isShortOutlet(now.typed) ? o.codes[0] : `${o.codes[0]}-`}</FamilyChip>
                                            </span>
                                            <input
                                                id={outletId}
                                                value={picked.outlet}
                                                maxLength={40}
                                                placeholder="Mont Kiara or MK"
                                                autoComplete="off"
                                                aria-invalid={outletError ? true : undefined}
                                                aria-describedby={outletError ? `${outletHelpId} ${outletErrorId}` : outletHelpId}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setPicked((p) => ({ ...p, outlet: value }));
                                                    if (outletError) setOutletError(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        void save();
                                                    }
                                                }}
                                                className={FIELD}
                                            />
                                        </div>
                                        {outletError && (
                                            <p id={outletErrorId} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                                                {outletError}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </Fragment>
                        );
                    })}
                </div>
                {/* Codes come from the name, then a pick, then the page title. */}
                {hasPick && !picked.internal && now.rows.length === 0 && doc.fileType === "gdoc" && (
                    <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>If line 1 of the doc carries a code, Oura files it there instead.</p>
                )}
                {error && (
                    <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-3")}>
                        {error}
                    </p>
                )}
            </DrawerBlock>
            <DrawerFoot
                first={(
                    <PillButton variant="primary" busy={busy} disabled={!changed} onClick={() => void save()}>
                        Save
                    </PillButton>
                )}
                second={(
                    <PillButton disabled={busy} onClick={onCancel}>
                        Cancel
                    </PillButton>
                )}
            />
        </>
    );
}
